import * as React from 'react'

import { readMutationError } from '../../../components/explorer'
import { Button } from '../../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import type { IngestRunRow } from '../util/groupIngestRuns'

interface RenameIngestRunDialogProps {
  run: IngestRunRow | null
  onClose: () => void
  onSubmit: (runId: string, name: string) => Promise<void>
}

export function RenameIngestRunDialog({ run, onClose, onSubmit }: RenameIngestRunDialogProps) {
  const [name, setName] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!run) return
    setName(run.runName ?? '')
    setSubmitting(false)
    setError(null)
  }, [run])

  const trimmed = name.trim()
  const tooLong = Array.from(trimmed).length > 100
  const valid = trimmed.length > 0 && !tooLong

  const submit = async () => {
    if (!run || !valid) return
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(run.id, trimmed)
      onClose()
    } catch (submitError) {
      setError(readMutationError(submitError, 'Could not save the ingest run name.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={run !== null}
      onOpenChange={(open) => (!open && !submitting ? onClose() : undefined)}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{run?.runName ? 'Rename ingest run' : 'Name ingest run'}</DialogTitle>
          <DialogDescription>
            Run names are unique within your organization and apply to every job in the run.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ingest-run-rename">Run name</Label>
          <Input
            id="ingest-run-rename"
            autoFocus
            value={name}
            disabled={submitting}
            aria-invalid={tooLong || Boolean(error)}
            onChange={(event) => {
              setName(event.target.value)
              setError(null)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                void submit()
              }
            }}
          />
          {tooLong ? <p className="text-xs text-st-down">Use 100 characters or fewer.</p> : null}
          {error ? (
            <p className="rounded-md bg-st-down-bg p-2.5 text-xs text-st-down" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!valid || submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
